/**
 * Pinterest Advertising Adapter (Stories 19.4 + 19.5)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 *
 * Story 19.4 wired OAuth + ad-account discovery; Story 19.5 wires the analytics
 * pipeline: 8 parallel sync `/analytics` + `/targeting_analytics` calls with
 * an async-report-templates fallback for >30-column or pin-promotion-level
 * fetches. Populates `PINTEREST_ADS_METRICS` (185 entries) and
 * `PINTEREST_ADS_DIMENSIONS` (30 entries) from
 * `_bmad/bmm/data/integration-metrics/pinterest-ads.md`. Widget presets and
 * fixture data are out of scope — those land in Story 19.6.
 *
 * **File-split rationale (Reviewer C concern, story 19.5 dev notes):**
 *   - pinterest-ads.ts        — adapter + metric/dimension catalogues + orchestration
 *   - pinterest/analytics.ts  — sync `/analytics` + `/targeting_analytics` HTTP
 *   - pinterest/reports.ts    — async report-templates POST→Poll→Download
 *   - pinterest/report-builders.ts — column lists + URL/body composers
 *   - pinterest/transformers.ts — Pinterest API → camelCase records
 *
 * google-ads.ts ships everything in one 1,200-line file; we deliberately
 * diverge to keep each Pinterest file <500 lines per the build-to-sell
 * directive ("a senior developer should understand within 60 seconds of
 * reading"). Same precedent as `microsoft/`.
 *
 * ADRs:
 *   - ADR-013 (Adapter Pattern): plain object implementing IntegrationAdapter
 *   - ADR-014 (API Versioning): `pinterestAds` lives in `API_VERSIONS`
 *   - ADR-016 (Aggressive Caching): 1h soft TTL via PLATFORM_CACHE_TTL
 *   - ADR-017 (Retry Logic): all HTTP calls flow through `safeFetch`
 *   - ADR-018 (Rate Limiter): Pinterest analytics 300 req/min/token; our worst case ~12 calls/fetchMetrics
 *   - ADR-020 (Token Lifecycle): continuous refresh tokens rotate every refresh — adapter persists rotated tokens
 *   - ADR-023 (Unified Metrics Interface): canonical schema; transformers normalise Pinterest-isms (`_1`/`_2`, micro-dollars, EENGAGEMENT_RATE)
 */

import type {
  AccountInfo,
  AdapterContext,
  AdapterErrorCode,
  AdapterMetrics,
  AdapterResult,
  DimensionDefinition,
  IntegrationAdapter,
  MetricDefinition,
  OAuthCredentials,
  RefreshedTokens,
} from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { API_VERSIONS } from "../config";
import { sanitizeErrorMessage } from "../safe-fetch";
import {
  fetchPinterestAdsAccountAnalytics,
  fetchPinterestAdsAdGroupsAnalytics,
  fetchPinterestAdsAdsAnalytics,
  fetchPinterestAdsCampaignsAnalytics,
  fetchPinterestAdsTargetingAnalytics,
  parsePinterestAdsSyncResponse,
  type PinterestAdsReportType,
} from "./analytics";
import {
  fetchPinterestAdsAdAccounts,
  fetchPinterestAdsUserAccount,
  PINTEREST_API_BASE,
  buildPinterestHeaders,
} from "./api";
import {
  PinterestAdsApiCallError,
  parsePinterestAdsApiError,
  pinterestAdsErrorTypeToAdapterCode,
} from "./errors";
import {
  PINTEREST_ADS_REFRESH_TOKEN_LIFETIME_MS,
  PINTEREST_ADS_SCOPES,
  PinterestAdsTokenError,
  refreshPinterestAdsToken,
} from "./oauth";
import {
  buildAsyncReportBody,
  buildSyncAnalyticsRequest,
  dateToYearMonthDay,
} from "./report-builders";
import {
  MAX_POLL_ATTEMPTS_LARGE,
  createPinterestAdsAsyncReport,
  downloadPinterestAdsAsyncReport,
  pollPinterestAdsAsyncReportUntilDone,
  type PinterestAdsAsyncReportLevel,
} from "./reports";
import { TRANSFORMERS } from "./transformers";
import { safeFetch } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

/** API version is exported for parity with other adapters' module-level constants. */
export const PINTEREST_ADS_API_VERSION = API_VERSIONS.pinterestAds;

/** Hard cap on a single fetchMetrics call to bound peak memory (Pre-mortem F2). */
export const MAX_FETCHMETRICS_DAYS = 90;

/**
 * The 8 sync report types Story 19.5 exercises in parallel via `Promise.all`.
 * Order is documentation-only.
 */
export const PINTEREST_ADS_REPORT_TYPES = [
  "overview",
  "campaigns",
  "adGroups",
  "ads",
  "keywords",
  "ageGender",
  "geo",
  "interests",
] as const;

/**
 * Compile-time guarantee the runtime tuple stays in sync with the
 * `PinterestAdsReportType` union. If a future story adds a 9th variant to
 * one without the other, TypeScript fails here.
 */
type _ReportTypeContractCheck = [
  (typeof PINTEREST_ADS_REPORT_TYPES)[number] extends PinterestAdsReportType ? true : false,
  PinterestAdsReportType extends (typeof PINTEREST_ADS_REPORT_TYPES)[number] ? true : false,
];
const _reportTypeContract: _ReportTypeContractCheck = [true, true];
void _reportTypeContract;

const REPORT_TYPES: ReadonlyArray<PinterestAdsReportType> = PINTEREST_ADS_REPORT_TYPES;

/** Levels routed to async-only — sync `/analytics` cannot reach these. */
const ASYNC_ONLY_LEVELS: ReadonlySet<string> = new Set<string>([
  "PIN_PROMOTION",
  "PRODUCT_GROUP",
  "PRODUCT_ITEM",
]);

/**
 * Per-pipeline error → severity classifier. Identical taxonomy to MS Ads'
 * `FATAL_ERROR_CODES`. Fatal errors abort the entire fetchMetrics; non-fatal
 * errors emit an empty `records` slice + a `warnings` entry.
 */
const FATAL_ERROR_CODES: ReadonlySet<AdapterErrorCode> = new Set([
  "token_expired",
  "token_invalid",
  "permission_denied",
  "api_not_enabled",
  "rate_limited",
  "provider_unavailable",
]);

/** Top-N selection caps for entity-scoped pipelines. */
const TOP_CAMPAIGNS_LIMIT = 20;
const TOP_AD_GROUPS_LIMIT = 20;
const TOP_ADS_LIMIT = 50;

// ============================================================================
// PINTEREST_ADS_METRICS — 185 entries (canonical from pinterest-ads.md)
// ============================================================================

const M = (
  key: string,
  label: string,
  type: MetricDefinition["type"],
  category: string,
  dataType: NonNullable<MetricDefinition["dataType"]>,
  compatibleWidgets: string[],
  description?: string,
  deprecated?: boolean,
): MetricDefinition => {
  const def: MetricDefinition = {
    key, label, type, category, dataType, compatibleWidgets,
    ...(description !== undefined ? { description } : {}),
    ...(deprecated ? { deprecated: true } : {}),
  };
  return def;
};

const PINTEREST_ADS_METRICS_BASE: MetricDefinition[] = [
  // ---------- Delivery & Reach (6) ----------
  M("paidImpressions", "Paid Impressions", "number", "delivery", "Integer", ["kpi", "line-chart", "bar-chart"], "Direct impressions of the promoted ad (advertiser billed)."),
  M("earnedImpressions", "Earned Impressions", "number", "delivery", "Integer", ["kpi", "line-chart"], "Impressions on organically repinned/saved copies (NOT billed)."),
  M("totalImpressions", "Total Impressions", "number", "delivery", "Integer", ["kpi", "line-chart"], "Sum of paid + earned impressions."),
  M("impressionFrequency", "Impression Frequency", "number", "delivery", "Decimal", ["kpi"], "Average impressions per unique user."),
  M("reachUniqueUsers", "Reach (Unique Users)", "number", "delivery", "Integer", ["kpi"], "Distinct users who saw any impression."),
  M("grossImpressions", "Gross Impressions", "number", "delivery", "Integer", ["kpi"], "Pinterest-internal gross paid impressions before viewability filtering."),

  // ---------- Spend & Cost Efficiency (9) ----------
  M("spendMicro", "Spend (micro)", "currency", "spend", "Currency", ["kpi", "line-chart", "bar-chart"], "Spend in advertiser currency (transformer divides micro-dollars by 1,000,000)."),
  M("spend", "Spend (dollars)", "currency", "spend", "Currency", ["kpi", "line-chart"], "Pre-rolled spend in advertiser currency."),
  M("ecpmMicro", "eCPM (micro)", "currency", "spend", "Currency", ["kpi"], "Effective cost per 1,000 impressions in advertiser currency."),
  M("ecpm", "eCPM (dollars)", "currency", "spend", "Currency", ["kpi"], "Effective cost per 1,000 impressions in advertiser currency."),
  M("cpm", "CPM (dollars)", "currency", "spend", "Currency", ["kpi"]),
  M("cpmMicro", "CPM (micro)", "currency", "spend", "Currency", ["kpi"]),
  M("paidCpc", "Paid CPC (dollars)", "currency", "spend", "Currency", ["kpi"]),
  M("paidCpcMicro", "Paid CPC (micro)", "currency", "spend", "Currency", ["kpi"]),
  M("costPerOutboundClick", "Cost Per Outbound Click", "currency", "spend", "Currency", ["kpi"]),

  // ---------- Engagement — Paid `_1` (6) ----------
  M("paidClicks", "Paid Pin Clicks", "number", "engagement_paid", "Integer", ["kpi", "bar-chart", "line-chart"], "Direct clicks on the promoted ad. Excludes clicks on saved/repinned copies (which are EARNED — see Earned Pin Clicks)."),
  M("paidCtr", "Paid CTR", "percentage", "engagement_paid", "Percentage", ["kpi", "gauge"], "Paid clicks ÷ paid impressions. Pinterest unsuffixed CTR is the paid variant."),
  M("paidOutboundClicks", "Paid Outbound Clicks", "number", "engagement_paid", "Integer", ["kpi", "line-chart"], "Outbound URL clicks attributed to paid impressions."),
  M("paidOutboundCtr", "Paid Outbound CTR", "percentage", "engagement_paid", "Percentage", ["kpi", "gauge"]),
  M("paidEngagements", "Paid Engagements", "number", "engagement_paid", "Integer", ["kpi", "bar-chart"]),
  M("paidEngagementRate", "Paid Engagement Rate", "percentage", "engagement_paid", "Percentage", ["kpi", "gauge"]),

  // ---------- Engagement — Earned `_2` (6) ----------
  M("earnedClicks", "Earned Pin Clicks", "number", "engagement_earned", "Integer", ["kpi"], "Clicks on organically repinned copies of the paid pin (NOT billed)."),
  M("earnedCtr", "Earned CTR", "percentage", "engagement_earned", "Percentage", ["kpi", "gauge"]),
  M("earnedCtrAlternate", "Earned CTR (alternate)", "percentage", "engagement_earned", "Percentage", ["kpi"], "Pinterest's `ECTR` field — same semantic as earnedCtr; some accounts only return one of the two."),
  M("earnedOutboundClicks", "Earned Outbound Clicks", "number", "engagement_earned", "Integer", ["kpi"]),
  M("earnedEngagements", "Earned Engagements", "number", "engagement_earned", "Integer", ["kpi"]),
  M("earnedEngagementRate", "Earned Engagement Rate", "percentage", "engagement_earned", "Percentage", ["kpi", "gauge"], "Pinterest's `EENGAGEMENT_RATE` (double-E) — earned engagements ÷ earned impressions."),

  // ---------- Engagement — Total Rollups (5) ----------
  M("totalClickthroughs", "Total Clickthroughs", "number", "engagement_total", "Integer", ["kpi", "line-chart"]),
  M("totalEngagements", "Total Engagements", "number", "engagement_total", "Integer", ["kpi", "line-chart"]),
  M("totalDestinationViews", "Total Destination Views", "number", "engagement_total", "Integer", ["kpi", "line-chart"]),
  M("totalRepinRate", "Total Repin Rate", "percentage", "engagement_total", "Percentage", ["kpi", "gauge"]),
  M("costPerEngagement", "Cost Per Engagement", "currency", "engagement_total", "Currency", ["kpi"]),

  // ---------- Saves (Repins) (5) ----------
  M("paidSaves", "Paid Saves (Repins)", "number", "saves", "Integer", ["kpi", "bar-chart"]),
  M("earnedSaves", "Earned Saves (Repins)", "number", "saves", "Integer", ["kpi"]),
  M("paidSaveRate", "Paid Save Rate", "percentage", "saves", "Percentage", ["kpi", "gauge"]),
  M("showcaseCloseupClicks", "Close-up Pin Clicks (Showcase)", "number", "saves", "Integer", ["kpi"]),
  M("showcaseAvgCloseupPerSession", "Avg Showcase Subpage Close-ups per Session", "number", "saves", "Decimal", ["kpi"]),

  // ---------- Video — Paid `_1` (10) ----------
  M("paidVideoMrcViews", "Video MRC Views (2s, 50% in-view) — Paid", "number", "video_paid", "Integer", ["kpi", "line-chart"]),
  M("paidVideo3sViews", "3-Second Video Views — Paid", "number", "video_paid", "Integer", ["kpi"]),
  M("paidVideoP0", "Video Start (0%) — Paid", "number", "video_paid", "Integer", ["kpi"]),
  M("paidVideoP25", "Video 25% Watched — Paid", "number", "video_paid", "Integer", ["kpi"]),
  M("paidVideoP50", "Video 50% Watched — Paid", "number", "video_paid", "Integer", ["kpi"]),
  M("paidVideoP75", "Video 75% Watched — Paid", "number", "video_paid", "Integer", ["kpi"]),
  M("paidVideoP95", "Video 95% Watched — Paid", "number", "video_paid", "Integer", ["kpi"]),
  M("paidVideoP100", "Video 100% Complete — Paid", "number", "video_paid", "Integer", ["kpi"]),
  M("paidVideoAvgWatchTimeSec", "Video Avg Watch Time — Paid", "duration", "video_paid", "Duration", ["kpi"]),
  M("videoLengthSec", "Video Length", "duration", "video_paid", "Duration", ["data-table"]),

  // ---------- Video — Earned `_2` (9) ----------
  M("earnedVideoMrcViews", "Video MRC Views — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideo3sViews", "3-Second Video Views — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideoP0", "Video Start (0%) — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideoP25", "Video 25% Watched — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideoP50", "Video 50% Watched — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideoP75", "Video 75% Watched — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideoP95", "Video 95% Watched — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideoP100", "Video 100% Complete — Earned", "number", "video_earned", "Integer", ["kpi"]),
  M("earnedVideoAvgWatchTimeSec", "Video Avg Watch Time — Earned", "duration", "video_earned", "Duration", ["kpi"]),

  // ---------- Video — Total Rollups (9) ----------
  M("totalVideo3sViews", "Total Video 3-Second Views", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoMrcViews", "Total Video MRC Views", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoP0", "Total Video Start (P0)", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoP25", "Total Video 25%", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoP50", "Total Video 50%", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoP75", "Total Video 75%", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoP95", "Total Video 95%", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoP100", "Total Video 100%", "number", "video_total", "Integer", ["kpi"]),
  M("totalVideoAvgWatchTimeSec", "Total Video Avg Watch Time", "duration", "video_total", "Duration", ["kpi"]),

  // ---------- Video — Cost Metrics (7) ----------
  M("cpvMicro", "Cost Per Video View (micro)", "currency", "video_cost", "Currency", ["kpi"]),
  M("ecpv", "Effective CPV", "currency", "video_cost", "Currency", ["kpi"]),
  M("cpcvMicro", "Cost Per Completed Video View (micro)", "currency", "video_cost", "Currency", ["kpi"]),
  M("ecpcv", "Effective CPCV", "currency", "video_cost", "Currency", ["kpi"]),
  M("cpcvP95Micro", "CPCV @ 95% (micro)", "currency", "video_cost", "Currency", ["kpi"]),
  M("ecpcvP95", "Effective CPCV @ 95%", "currency", "video_cost", "Currency", ["kpi"]),
  M("paidVideoViewableRate", "Paid Video Viewable Rate", "percentage", "video_cost", "Percentage", ["kpi", "gauge"]),

  // ---------- Conversions — Click-Attributed (10) ----------
  M("totalClickConversions", "Total Click Conversions", "number", "conversions_click", "Integer", ["kpi", "line-chart"]),
  M("clickPageVisit", "Click Page Visit", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickSignup", "Click Signup", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickCheckout", "Click Checkout", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickAddToCart", "Click Add to Cart", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickLead", "Click Lead", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickSearch", "Click Search", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickCustom", "Click Custom", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickWatchVideo", "Click Watch Video", "number", "conversions_click", "Integer", ["kpi"]),
  M("clickCheckoutValue", "Click Checkout Value (micro)", "currency", "conversions_click", "Currency", ["kpi"]),

  // ---------- Conversions — View-Attributed (7) ----------
  M("viewPageVisit", "View Page Visit", "number", "conversions_view", "Integer", ["kpi"]),
  M("viewSignup", "View Signup", "number", "conversions_view", "Integer", ["kpi"]),
  M("viewCheckout", "View Checkout", "number", "conversions_view", "Integer", ["kpi"]),
  M("viewAddToCart", "View Add to Cart", "number", "conversions_view", "Integer", ["kpi"]),
  M("viewCustom", "View Custom", "number", "conversions_view", "Integer", ["kpi"]),
  M("viewCategory", "View Category", "number", "conversions_view", "Integer", ["kpi"]),
  M("viewCheckoutValue", "View Checkout Value (micro)", "currency", "conversions_view", "Currency", ["kpi"]),

  // ---------- Conversions — Engagement-Attributed (3) ----------
  M("engagementPageVisit", "Engagement Page Visit", "number", "conversions_engagement", "Integer", ["kpi"]),
  M("engagementCheckout", "Engagement Checkout", "number", "conversions_engagement", "Integer", ["kpi"]),
  M("engagementSignup", "Engagement Signup", "number", "conversions_engagement", "Integer", ["kpi"]),

  // ---------- Conversion Rollups — Total (12) ----------
  M("totalPageVisits", "Total Page Visits", "number", "conversions_total", "Integer", ["kpi", "line-chart"]),
  M("totalSignups", "Total Signups", "number", "conversions_total", "Integer", ["kpi", "line-chart"]),
  M("totalCheckouts", "Total Checkouts", "number", "conversions_total", "Integer", ["kpi", "line-chart"]),
  M("totalCustomEvents", "Total Custom Events", "number", "conversions_total", "Integer", ["kpi"]),
  M("totalAddToCart", "Total Add to Cart", "number", "conversions_total", "Integer", ["kpi", "line-chart"]),
  M("totalLeads", "Total Leads", "number", "conversions_total", "Integer", ["kpi", "line-chart"]),
  M("totalSearches", "Total Searches", "number", "conversions_total", "Integer", ["kpi"]),
  M("totalWatchVideoEvents", "Total Watch Video Events", "number", "conversions_total", "Integer", ["kpi"]),
  M("totalViewCategoryEvents", "Total View Category Events", "number", "conversions_total", "Integer", ["kpi"]),
  M("totalAppInstalls", "Total App Installs", "number", "conversions_total", "Integer", ["kpi"]),
  M("totalConversionsQuantity", "Total Conversions Quantity", "number", "conversions_total", "Integer", ["kpi"]),
  M("totalConversionsValue", "Total Conversion Value (micro)", "currency", "conversions_total", "Currency", ["kpi"]),

  // ---------- Conversion Rates (10) ----------
  M("checkoutConversionRate", "Checkout Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("addToCartConversionRate", "Add to Cart Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("signupConversionRate", "Signup Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("pageVisitConversionRate", "Page Visit Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("leadConversionRate", "Lead Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("searchConversionRate", "Search Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("watchVideoConversionRate", "Watch Video Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("customConversionRate", "Custom Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("viewCategoryConversionRate", "View Category Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi", "gauge"]),
  M("unknownConversionRate", "Unknown Conversion Rate", "percentage", "conversion_rates", "Percentage", ["kpi"]),

  // ---------- Revenue Rollups — Total Value (8) ----------
  M("totalCheckoutValue", "Total Checkout Value", "currency", "revenue", "Currency", ["kpi", "line-chart"]),
  M("totalCustomEventValue", "Total Custom Event Value", "currency", "revenue", "Currency", ["kpi"]),
  M("totalAddToCartValue", "Total Add to Cart Value", "currency", "revenue", "Currency", ["kpi"]),
  M("totalLeadValue", "Total Lead Value", "currency", "revenue", "Currency", ["kpi"]),
  M("totalSearchValue", "Total Search Value", "currency", "revenue", "Currency", ["kpi"]),
  M("totalPageVisitValue", "Total Page Visit Value", "currency", "revenue", "Currency", ["kpi"]),
  M("totalWatchVideoValue", "Total Watch Video Value", "currency", "revenue", "Currency", ["kpi"]),
  M("totalSignupValue", "Total Signup Value", "currency", "revenue", "Currency", ["kpi"]),

  // ---------- Cost Per Action & ROAS (5) ----------
  M("pageVisitCostPerAction", "Page Visit Cost Per Action", "currency", "cost_per_action", "Currency", ["kpi"]),
  M("pageVisitRoas", "Page Visit ROAS", "number", "cost_per_action", "Ratio", ["kpi", "gauge"]),
  M("checkoutRoas", "Checkout ROAS", "number", "cost_per_action", "Ratio", ["kpi", "gauge"]),
  M("customEventRoas", "Custom Event ROAS", "number", "cost_per_action", "Ratio", ["kpi", "gauge"]),
  M("appInstallCostPerAction", "App Install Cost Per Action", "currency", "cost_per_action", "Currency", ["kpi"]),

  // ---------- Channel — Web (10) ----------
  M("totalWebCheckouts", "Total Web Checkouts", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebAddToCart", "Total Web Add to Cart", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebPageVisits", "Total Web Page Visits", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebSignups", "Total Web Signups", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebWatchVideo", "Total Web Watch Video", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebLeads", "Total Web Leads", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebSearches", "Total Web Searches", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebViewCategory", "Total Web View Category", "number", "channel_web", "Integer", ["kpi"]),
  M("totalWebCustom", "Total Web Custom", "number", "channel_web", "Integer", ["kpi"]),
  M("webCheckoutRoas", "Web Checkout ROAS", "number", "channel_web", "Ratio", ["kpi", "gauge"]),

  // ---------- Channel — In-App (6) ----------
  M("totalInappCheckouts", "Total In-App Checkouts", "number", "channel_inapp", "Integer", ["kpi"]),
  M("totalInappAddToCart", "Total In-App Add to Cart", "number", "channel_inapp", "Integer", ["kpi"]),
  M("totalInappSignups", "Total In-App Signups", "number", "channel_inapp", "Integer", ["kpi"]),
  M("totalInappSearches", "Total In-App Searches", "number", "channel_inapp", "Integer", ["kpi"]),
  M("totalInappAppInstalls", "Total In-App App Installs", "number", "channel_inapp", "Integer", ["kpi"]),
  M("inappCheckoutRoas", "In-App Checkout ROAS", "number", "channel_inapp", "Ratio", ["kpi", "gauge"]),

  // ---------- Channel — Offline (5) ----------
  M("totalOfflineCheckouts", "Total Offline Checkouts", "number", "channel_offline", "Integer", ["kpi"]),
  M("totalOfflineSignups", "Total Offline Signups", "number", "channel_offline", "Integer", ["kpi"]),
  M("totalOfflineLeads", "Total Offline Leads", "number", "channel_offline", "Integer", ["kpi"]),
  M("totalOfflineCustom", "Total Offline Custom", "number", "channel_offline", "Integer", ["kpi"]),
  M("offlineCheckoutRoas", "Offline Checkout ROAS", "number", "channel_offline", "Ratio", ["kpi", "gauge"]),

  // ---------- Channel — Pinterest-Native (3) ----------
  M("totalPinterestCheckouts", "Total Pinterest Checkouts", "number", "channel_pinterest", "Integer", ["kpi"]),
  M("totalPinterestCheckoutValue", "Total Pinterest Checkout Value", "currency", "channel_pinterest", "Currency", ["kpi"]),
  M("pinterestCheckoutRoas", "Pinterest Checkout ROAS", "number", "channel_pinterest", "Ratio", ["kpi", "gauge"]),

  // ---------- Cross-Device Attribution Matrix sample (5) ----------
  M("pageVisitDesktopToDesktop", "Page Visit — Desktop to Desktop", "number", "cross_device", "Integer", ["data-table"]),
  M("pageVisitDesktopToMobile", "Page Visit — Desktop to Mobile", "number", "cross_device", "Integer", ["data-table"]),
  M("pageVisitMobileToDesktop", "Page Visit — Mobile to Desktop", "number", "cross_device", "Integer", ["data-table"]),
  M("checkoutDesktopToDesktop", "Checkout — Desktop to Desktop", "number", "cross_device", "Integer", ["data-table"]),
  M("checkoutMobileToMobile", "Checkout — Mobile to Mobile", "number", "cross_device", "Integer", ["data-table"]),

  // ---------- Web Sessions (3) ----------
  M("totalWebSessions", "Total Web Sessions", "number", "web_sessions", "Integer", ["kpi", "line-chart"]),
  M("paidWebSessions", "Paid Web Sessions", "number", "web_sessions", "Integer", ["kpi"]),
  M("earnedWebSessions", "Earned Web Sessions", "number", "web_sessions", "Integer", ["kpi"]),

  // ---------- Leads (2) ----------
  M("leads", "Leads", "number", "leads", "Integer", ["kpi", "line-chart"]),
  M("costPerLead", "Cost Per Lead", "currency", "leads", "Currency", ["kpi"]),

  // ---------- Quantity Variants sample (5) ----------
  M("totalCheckoutQuantity", "Total Checkout Quantity", "number", "quantity", "Integer", ["kpi"]),
  M("totalClickPageVisitQuantity", "Total Click Page Visit Quantity", "number", "quantity", "Integer", ["kpi"]),
  M("totalViewCheckoutQuantity", "Total View Checkout Quantity", "number", "quantity", "Integer", ["kpi"]),
  M("totalEngagementAddToCartQuantity", "Total Engagement Add to Cart Quantity", "number", "quantity", "Integer", ["kpi"]),
  M("totalClickCheckoutQuantity", "Total Click Checkout Quantity", "number", "quantity", "Integer", ["kpi"]),

  // ---------- Idea Pin (3) ----------
  M("paidIdeaPinProductTagVisits", "Paid Idea Pin Product Tag Visits", "number", "idea_pin", "Integer", ["kpi"]),
  M("earnedIdeaPinProductTagVisits", "Earned Idea Pin Product Tag Visits", "number", "idea_pin", "Integer", ["kpi"]),
  M("totalIdeaPinProductTagVisits", "Total Idea Pin Product Tag Visits", "number", "idea_pin", "Integer", ["kpi"]),

  // ---------- Quiz (2) ----------
  M("quizCompleted", "Quiz Completed", "number", "quiz", "Integer", ["kpi"]),
  M("quizCompletionRate", "Quiz Completion Rate", "percentage", "quiz", "Percentage", ["kpi", "gauge"]),

  // ---------- Showcase (14) ----------
  M("showcasePinClickthroughs", "Showcase Pin Clickthroughs", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpageClickthroughs", "Showcase Subpage Clickthroughs", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpinClickthroughs", "Showcase Sub-pin Clickthroughs", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpageImpressions", "Showcase Subpage Impressions", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpinImpressions", "Showcase Sub-pin Impressions", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpageSwipeLeft", "Showcase Subpage Swipe Left", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpageSwipeRight", "Showcase Subpage Swipe Right", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpinSwipeLeft", "Showcase Sub-pin Swipe Left", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpinSwipeRight", "Showcase Sub-pin Swipe Right", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpageRepins", "Showcase Subpage Repins", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseSubpinRepins", "Showcase Sub-pin Repins", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseCardThumbnailSwipeForward", "Showcase Card Thumbnail Swipe Forward", "number", "showcase", "Integer", ["kpi"]),
  M("showcaseCardThumbnailSwipeBackward", "Showcase Card Thumbnail Swipe Backward", "number", "showcase", "Integer", ["kpi"]),
  M("paidOnsiteCheckouts", "Onsite Checkouts (paid)", "number", "showcase", "Integer", ["kpi"]),

];

// ============================================================================
// Story 31-31 — aggregation annotations (multi-attachment widgets, ADR-0004)
// ============================================================================
// The positional `M(...)` builder cannot carry the new optional fields without
// churning every call signature, so annotations are composed AFTER the base
// catalogue (sanctioned by Story 31-31 AC #4: "extend the M(...) helper or
// compose after"). Every metric key MUST appear below — omissions fail the
// registry-wide gap report in `metric-registry.aggregation.test.ts`.
//   A  = additive (values sum across attached resources)
//   R  = ratio with the natural weight as denominator (weighted average)
//   RU = ratio with NO catalog weight -> UNWEIGHTED_RATIO_ALLOWLIST entry
type AggregationAnnotation = Pick<
  MetricDefinition,
  "aggregationKind" | "ratioComponents"
>;
const A: AggregationAnnotation = { aggregationKind: "additive" };
const RU: AggregationAnnotation = { aggregationKind: "ratio" };
const R = (denominator: string, numerator?: string): AggregationAnnotation => ({
  aggregationKind: "ratio",
  ratioComponents: {
    ...(numerator !== undefined ? { numerator } : {}),
    denominator,
  },
});

const PINTEREST_ADS_METRICS_AGGREGATION: Readonly<Record<string, AggregationAnnotation>> = {
  paidImpressions: A,
  earnedImpressions: A,
  totalImpressions: A,
  impressionFrequency: R("reachUniqueUsers", "paidImpressions"),
  reachUniqueUsers: A,
  grossImpressions: A,
  spendMicro: A,
  spend: A,
  ecpmMicro: R("paidImpressions", "spendMicro"),
  ecpm: R("paidImpressions", "spend"),
  cpm: R("paidImpressions", "spend"),
  cpmMicro: R("paidImpressions", "spendMicro"),
  paidCpc: R("paidClicks", "spend"),
  paidCpcMicro: R("paidClicks", "spendMicro"),
  costPerOutboundClick: R("paidOutboundClicks", "spend"),
  paidClicks: A,
  paidCtr: R("paidImpressions", "paidClicks"),
  paidOutboundClicks: A,
  paidOutboundCtr: R("paidImpressions", "paidOutboundClicks"),
  paidEngagements: A,
  paidEngagementRate: R("paidImpressions", "paidEngagements"),
  earnedClicks: A,
  earnedCtr: R("earnedImpressions", "earnedClicks"),
  earnedCtrAlternate: R("earnedImpressions"),
  earnedOutboundClicks: A,
  earnedEngagements: A,
  earnedEngagementRate: R("earnedImpressions", "earnedEngagements"),
  totalClickthroughs: A,
  totalEngagements: A,
  totalDestinationViews: A,
  totalRepinRate: R("totalImpressions"),
  costPerEngagement: R("paidEngagements", "spend"),
  paidSaves: A,
  earnedSaves: A,
  paidSaveRate: R("paidImpressions", "paidSaves"),
  showcaseCloseupClicks: A,
  showcaseAvgCloseupPerSession: RU,
  paidVideoMrcViews: A,
  paidVideo3sViews: A,
  paidVideoP0: A,
  paidVideoP25: A,
  paidVideoP50: A,
  paidVideoP75: A,
  paidVideoP95: A,
  paidVideoP100: A,
  paidVideoAvgWatchTimeSec: R("paidVideoMrcViews"),
  videoLengthSec: RU,
  earnedVideoMrcViews: A,
  earnedVideo3sViews: A,
  earnedVideoP0: A,
  earnedVideoP25: A,
  earnedVideoP50: A,
  earnedVideoP75: A,
  earnedVideoP95: A,
  earnedVideoP100: A,
  earnedVideoAvgWatchTimeSec: R("earnedVideoMrcViews"),
  totalVideo3sViews: A,
  totalVideoMrcViews: A,
  totalVideoP0: A,
  totalVideoP25: A,
  totalVideoP50: A,
  totalVideoP75: A,
  totalVideoP95: A,
  totalVideoP100: A,
  totalVideoAvgWatchTimeSec: R("totalVideoMrcViews"),
  cpvMicro: R("paidVideoMrcViews", "spendMicro"),
  ecpv: R("paidVideoMrcViews", "spend"),
  cpcvMicro: R("paidVideoP100", "spendMicro"),
  ecpcv: R("paidVideoP100", "spend"),
  cpcvP95Micro: R("paidVideoP95", "spendMicro"),
  ecpcvP95: R("paidVideoP95", "spend"),
  paidVideoViewableRate: R("paidImpressions"),
  totalClickConversions: A,
  clickPageVisit: A,
  clickSignup: A,
  clickCheckout: A,
  clickAddToCart: A,
  clickLead: A,
  clickSearch: A,
  clickCustom: A,
  clickWatchVideo: A,
  clickCheckoutValue: A,
  viewPageVisit: A,
  viewSignup: A,
  viewCheckout: A,
  viewAddToCart: A,
  viewCustom: A,
  viewCategory: A,
  viewCheckoutValue: A,
  engagementPageVisit: A,
  engagementCheckout: A,
  engagementSignup: A,
  totalPageVisits: A,
  totalSignups: A,
  totalCheckouts: A,
  totalCustomEvents: A,
  totalAddToCart: A,
  totalLeads: A,
  totalSearches: A,
  totalWatchVideoEvents: A,
  totalViewCategoryEvents: A,
  totalAppInstalls: A,
  totalConversionsQuantity: A,
  totalConversionsValue: A,
  checkoutConversionRate: R("paidClicks", "totalCheckouts"),
  addToCartConversionRate: R("paidClicks", "totalAddToCart"),
  signupConversionRate: R("paidClicks", "totalSignups"),
  pageVisitConversionRate: R("paidClicks", "totalPageVisits"),
  leadConversionRate: R("paidClicks", "totalLeads"),
  searchConversionRate: R("paidClicks", "totalSearches"),
  watchVideoConversionRate: R("paidClicks", "totalWatchVideoEvents"),
  customConversionRate: R("paidClicks", "totalCustomEvents"),
  viewCategoryConversionRate: R("paidClicks", "totalViewCategoryEvents"),
  unknownConversionRate: R("paidClicks"),
  totalCheckoutValue: A,
  totalCustomEventValue: A,
  totalAddToCartValue: A,
  totalLeadValue: A,
  totalSearchValue: A,
  totalPageVisitValue: A,
  totalWatchVideoValue: A,
  totalSignupValue: A,
  pageVisitCostPerAction: R("totalPageVisits", "spend"),
  pageVisitRoas: R("spend"),
  checkoutRoas: R("spend"),
  customEventRoas: R("spend"),
  appInstallCostPerAction: R("totalAppInstalls", "spend"),
  totalWebCheckouts: A,
  totalWebAddToCart: A,
  totalWebPageVisits: A,
  totalWebSignups: A,
  totalWebWatchVideo: A,
  totalWebLeads: A,
  totalWebSearches: A,
  totalWebViewCategory: A,
  totalWebCustom: A,
  webCheckoutRoas: R("spend"),
  totalInappCheckouts: A,
  totalInappAddToCart: A,
  totalInappSignups: A,
  totalInappSearches: A,
  totalInappAppInstalls: A,
  inappCheckoutRoas: R("spend"),
  totalOfflineCheckouts: A,
  totalOfflineSignups: A,
  totalOfflineLeads: A,
  totalOfflineCustom: A,
  offlineCheckoutRoas: R("spend"),
  totalPinterestCheckouts: A,
  totalPinterestCheckoutValue: A,
  pinterestCheckoutRoas: R("spend"),
  pageVisitDesktopToDesktop: A,
  pageVisitDesktopToMobile: A,
  pageVisitMobileToDesktop: A,
  checkoutDesktopToDesktop: A,
  checkoutMobileToMobile: A,
  totalWebSessions: A,
  paidWebSessions: A,
  earnedWebSessions: A,
  leads: A,
  costPerLead: R("leads", "spend"),
  totalCheckoutQuantity: A,
  totalClickPageVisitQuantity: A,
  totalViewCheckoutQuantity: A,
  totalEngagementAddToCartQuantity: A,
  totalClickCheckoutQuantity: A,
  paidIdeaPinProductTagVisits: A,
  earnedIdeaPinProductTagVisits: A,
  totalIdeaPinProductTagVisits: A,
  quizCompleted: A,
  quizCompletionRate: RU,
  showcasePinClickthroughs: A,
  showcaseSubpageClickthroughs: A,
  showcaseSubpinClickthroughs: A,
  showcaseSubpageImpressions: A,
  showcaseSubpinImpressions: A,
  showcaseSubpageSwipeLeft: A,
  showcaseSubpageSwipeRight: A,
  showcaseSubpinSwipeLeft: A,
  showcaseSubpinSwipeRight: A,
  showcaseSubpageRepins: A,
  showcaseSubpinRepins: A,
  showcaseCardThumbnailSwipeForward: A,
  showcaseCardThumbnailSwipeBackward: A,
  paidOnsiteCheckouts: A,
};

export const PINTEREST_ADS_METRICS: MetricDefinition[] = PINTEREST_ADS_METRICS_BASE.map((m) => {
  const agg = PINTEREST_ADS_METRICS_AGGREGATION[m.key];
  return agg ? { ...m, ...agg } : m;
});


/**
 * Sync-non-queryable metric keys — surfaced separately so the Widget Edit
 * Panel can warn callers (per AC #6). These 2 fields exist in Pinterest's
 * full enum (`TOTAL_VIEW_LEAD`, `TOTAL_ENGAGEMENT_LEAD`) but Pinterest 400s
 * if a sync analytics request includes them; only async report templates can
 * fetch them.
 */
export const PINTEREST_ADS_SYNC_NON_QUERYABLE_METRICS = [
  "totalViewLead",
  "totalEngagementLead",
] as const;

// ============================================================================
// PINTEREST_ADS_DIMENSIONS — 30 entries
// ============================================================================

const D = (
  key: string,
  label: string,
  type: DimensionDefinition["type"],
  exampleValues?: string[],
): DimensionDefinition =>
  exampleValues ? { key, label, type, exampleValues } : { key, label, type };

export const PINTEREST_ADS_DIMENSIONS: DimensionDefinition[] = [
  // Time (5)
  D("granularityTotal", "Total (no time breakdown)", "Time"),
  D("granularityHour", "Hour", "Time", ["00:00", "13:00"]),
  D("granularityDay", "Day", "Time", ["2026-04-01", "2026-04-02"]),
  D("granularityWeek", "Week", "Time", ["2026-W17"]),
  D("granularityMonth", "Month", "Time", ["2026-04"]),
  // Structure (6)
  D("adAccount", "Ad Account", "Structure"),
  D("campaign", "Campaign", "Structure"),
  D("adGroup", "Ad Group", "Structure"),
  D("ad", "Ad", "Structure"),
  D("productGroup", "Product Group", "Structure"),
  D("pinPromotion", "Pin (Promoted)", "Structure"),
  // Targeting (13)
  D("keyword", "Keyword", "Search"),
  D("appType", "App Type (Platform)", "Tech", ["web", "mobile-web", "android", "iOS"]),
  D("gender", "Gender", "Demo"),
  D("ageBucket", "Age Bucket", "Demo", ["18-24", "25-34"]),
  D("ageBucketAndGender", "Age Bucket + Gender (BETA)", "Demo"),
  D("location", "Location", "Geo"),
  D("region", "Region", "Geo"),
  D("country", "Country", "Geo", ["US", "GB"]),
  D("geo", "Geo", "Geo"),
  D("placement", "Placement", "Delivery", ["browse", "search", "other"]),
  D("targetedInterest", "Targeted Interest", "Audience"),
  D("pinnerInterest", "Pinner Interest", "Audience"),
  D("audienceInclude", "Audience Include", "Audience"),
  // Content & Creative (6)
  D("pinCreativeType", "Pin Creative Type", "Creative", ["REGULAR", "VIDEO", "CAROUSEL", "IDEA", "SHOPPING"]),
  D("objectiveType", "Objective Type", "Classification", ["AWARENESS", "CONSIDERATION", "CONVERSIONS"]),
  D("productItemId", "Product Item ID", "Product"),
  D("productCategory", "Product Category", "Product"),
  D("placementChannel", "Placement (Browse / Search / Other)", "Delivery"),
  D("conversionEventType", "Conversion Event Type", "Conversion"),
];

// ============================================================================
// Pipeline result discriminated union (shared by sync + async pipelines)
// ============================================================================

type PipelineSuccess = {
  ok: true;
  reportType: PinterestAdsReportType;
  records: Array<Record<string, unknown>>;
};
type PipelineWarning = {
  ok: false;
  fatal: false;
  reportType: PinterestAdsReportType;
  reason: string;
};
type PipelineFatal = {
  ok: false;
  fatal: true;
  reportType: PinterestAdsReportType;
  code: AdapterErrorCode;
  message: string;
  retryable: boolean;
  retryAfterMs?: number;
};
type PipelineResult = PipelineSuccess | PipelineWarning | PipelineFatal;

// ============================================================================
// Adapter
// ============================================================================

export const pinterestAdsAdapter: IntegrationAdapter = {
  platform: "pinterest-ads",

  rateLimits: {
    requestsPerMinute: 60,
    retryAfterMs: 1000,
  },

  /**
   * Story 19.5 implementation — 8 parallel sync pipelines + async-on-overflow
   * fallback. See dev notes in `19-5-pinterest-ads-adapter.md` for the
   * complete decision tree.
   */
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date },
  ): Promise<AdapterResult<AdapterMetrics>> {
    // -------- 1. Fixture mode short-circuit (Story 19.6 AD-19.6-04) ----------
    // MUST run before the live-mode account-ID regex check at step 2 — fixture
    // mode bypasses live-mode validation per the FDL-3 contract. The demo
    // account_id values like "demo-900003" do NOT match Pinterest's `/^\d+$/`
    // numeric-only shape, but they never reach the API: the helper reads from
    // KV via the clientSlug-keyed fixture and returns adapter-shaped output.
    const fixtures = (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixtures) {
      return loadPinterestAdsFixture(ctx, dateRange);
    }

    // -------- 2. Account ID parse (AC #1) ------------------------------------
    const accountId = ctx.accountId ?? "";
    if (!/^\d+$/.test(accountId)) {
      return adapterErr({
        code: "account_not_found",
        message: "Pinterest Ads account ID must be a numeric ad-account ID string",
        retryable: false,
      });
    }

    // -------- 3. Date range validation (AC #1, Pre-mortem F2) ----------------
    if (!(dateRange.start instanceof Date) || !(dateRange.end instanceof Date)) {
      return adapterErr({
        code: "invalid_response",
        message: "Pinterest Ads dateRange.start and dateRange.end must be Date objects",
        retryable: false,
      });
    }
    if (Number.isNaN(dateRange.start.getTime()) || Number.isNaN(dateRange.end.getTime())) {
      return adapterErr({
        code: "invalid_response",
        message: "Pinterest Ads dateRange contains invalid Date values",
        retryable: false,
      });
    }
    if (dateRange.start.getTime() > dateRange.end.getTime()) {
      return adapterErr({
        code: "invalid_response",
        message: "Pinterest Ads dateRange.start must be on or before dateRange.end",
        retryable: false,
      });
    }
    // Inclusive calendar-day count, normalised to UTC midnight first so
    // end-of-day timestamps don't push the count over by one (e.g.
    // start=2026-01-01T00:00Z, end=2026-03-31T23:59Z is "90 days" not 91).
    const startMidnightUtc = Date.UTC(
      dateRange.start.getUTCFullYear(),
      dateRange.start.getUTCMonth(),
      dateRange.start.getUTCDate(),
    );
    const endMidnightUtc = Date.UTC(
      dateRange.end.getUTCFullYear(),
      dateRange.end.getUTCMonth(),
      dateRange.end.getUTCDate(),
    );
    const days =
      Math.round((endMidnightUtc - startMidnightUtc) / (24 * 60 * 60 * 1000)) + 1;
    if (days > MAX_FETCHMETRICS_DAYS) {
      return adapterErr({
        code: "invalid_response",
        message: `Pinterest Ads fetchMetrics is capped at ${MAX_FETCHMETRICS_DAYS} days; split larger ranges in the queue consumer`,
        retryable: false,
      });
    }

    // -------- 4. Async fallback detection (AC #9) ----------------------------
    const requestedLevel = ctx.requestedLevel;
    const requestedColumns = ctx.requestedColumns;
    const asyncFallback =
      (requestedLevel !== undefined && ASYNC_ONLY_LEVELS.has(requestedLevel)) ||
      (requestedColumns !== undefined && requestedColumns.length > 30);

    // -------- 5. Fetch ad-account metadata + parallel ID lists (AC #8, #10) ---
    let accountMeta: { currency: string; timeZone: string };
    try {
      accountMeta = await fetchAdAccountMetadata(credentials.accessToken, accountId);
    } catch (err) {
      const code = adapterErrorCodeFromError(err);
      if (FATAL_ERROR_CODES.has(code)) {
        return adapterErr({
          code,
          message: sanitizeErrorMessage(
            `Pinterest Ads ad-account metadata fetch failed: ${err instanceof Error ? err.message : String(err)}`,
          ),
          retryable: code === "rate_limited" || code === "provider_unavailable",
        });
      }
      // Non-fatal — keep going with empty metadata
      accountMeta = { currency: "", timeZone: "" };
    }

    let topCampaignIds: string[] = [];
    let topAdGroupIds: string[] = [];
    let topAdIds: string[] = [];
    try {
      const ids = await fetchTopEntityIds(credentials.accessToken, accountId, dateRange);
      topCampaignIds = ids.campaigns;
      topAdGroupIds = ids.adGroups;
      topAdIds = ids.ads;
    } catch (err) {
      const code = adapterErrorCodeFromError(err);
      if (FATAL_ERROR_CODES.has(code)) {
        return adapterErr({
          code,
          message: sanitizeErrorMessage(
            `Pinterest Ads entity-list fetch failed: ${err instanceof Error ? err.message : String(err)}`,
          ),
          retryable: code === "rate_limited" || code === "provider_unavailable",
        });
      }
      // Non-fatal — proceed with empty top-N (entity-scoped pipelines will surface warnings)
    }

    // -------- 6. Run 8 pipelines in parallel ---------------------------------
    // Async fallback applies ONLY to entity-scoped report types (overview /
    // campaigns / adGroups / ads) — Pinterest's async report templates take a
    // `level` parameter and have no equivalent for `targeting_types`. The
    // 4 targeting reports (keywords / ageGender / geo / interests) ALWAYS
    // run sync because the async API cannot model them.
    const ENTITY_REPORT_TYPES: ReadonlySet<PinterestAdsReportType> = new Set([
      "overview",
      "campaigns",
      "adGroups",
      "ads",
    ]);
    const pipelines: Array<Promise<PipelineResult>> = REPORT_TYPES.map((reportType) => {
      if (asyncFallback && ENTITY_REPORT_TYPES.has(reportType)) {
        return runAsyncPipeline({
          reportType,
          accessToken: credentials.accessToken,
          accountId,
          dateRange,
          requestedLevel,
          requestedColumns,
          topCampaignIds,
          topAdGroupIds,
          topAdIds,
        });
      }
      return runSyncPipeline({
        reportType,
        accessToken: credentials.accessToken,
        accountId,
        dateRange,
        topCampaignIds,
        topAdGroupIds,
        topAdIds,
      });
    });
    const settled = await Promise.all(pipelines);

    // -------- 7. Classify fatal vs warning (AC #4) --------------------------
    const allFatals = settled.filter((r): r is PipelineFatal => !r.ok && r.fatal);
    if (allFatals.length > 0) {
      const PRIORITY: AdapterErrorCode[] = [
        "token_expired",
        "token_invalid",
        "permission_denied",
        "api_not_enabled",
        "rate_limited",
        "provider_unavailable",
      ];
      const sorted = [...allFatals].sort(
        (a, b) => PRIORITY.indexOf(a.code) - PRIORITY.indexOf(b.code),
      );
      const fatal = sorted[0]!;
      return adapterErr({
        code: fatal.code,
        message: fatal.message,
        retryable: fatal.retryable,
        retryAfterMs: fatal.retryAfterMs,
      });
    }

    const warnings: Array<{ reportType: string; reason: string }> = [];
    const records: Array<Record<string, unknown>> = [];
    for (const r of settled) {
      if (r.ok) {
        // Static contract check (Pre-mortem F4) — every record must carry a non-empty _reportType.
        for (const rec of r.records) {
          if (!rec._reportType || typeof rec._reportType !== "string") {
            return adapterErr({
              code: "invalid_response",
              message: "Pinterest Ads transformer emitted a record without _reportType",
              retryable: false,
            });
          }
        }
        records.push(...r.records);
      } else if (!r.fatal) {
        warnings.push({ reportType: r.reportType, reason: r.reason });
      }
    }

    // -------- 8. Compose AdapterMetrics (AC #5, #8) -------------------------
    const metrics: AdapterMetrics = {
      platform: "pinterest-ads",
      accountId,
      dateRange,
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
      warnings: warnings.length > 0 ? warnings : undefined,
      metadata: {
        adAccountId: accountId,
        currencyCode: accountMeta.currency || undefined,
        timeZone: accountMeta.timeZone || undefined,
      },
    };
    return adapterOk(metrics);
  },

  /** Story 19.4 implementation — UNCHANGED. */
  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<boolean>> {
    try {
      const userAccount = await fetchPinterestAdsUserAccount(credentials.accessToken);
      const result = await fetchPinterestAdsAdAccounts(credentials.accessToken, {
        pageSize: 1,
        maxPages: 1,
      });
      if (result.accounts.length === 0) {
        const friendlyMessage =
          userAccount.accountType !== "BUSINESS"
            ? "This Pinterest account isn't linked to any Pinterest Ad Accounts. Sign in with a Pinterest Business account that owns or has Business Access to at least one ad account."
            : "Pinterest account is not linked to any Pinterest Ad Accounts";
        return adapterErr({
          code: "account_not_found",
          message: friendlyMessage,
          retryable: false,
        });
      }
      return adapterOk(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeErrorMessage(`Pinterest Ads connection validation failed: ${message}`),
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  /** Story 19.4 implementation — UNCHANGED. */
  async refreshToken(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_invalid",
        message: "No refresh token available for Pinterest Ads",
        retryable: false,
      });
    }

    const env = ctx.env as unknown as Record<string, string | undefined>;
    const clientId = env.PINTEREST_ADS_CLIENT_ID ?? "";
    const clientSecret = env.PINTEREST_ADS_CLIENT_SECRET ?? "";

    if (!clientId || !clientSecret) {
      return adapterErr({
        code: "token_invalid",
        message: "Missing PINTEREST_ADS_CLIENT_ID or PINTEREST_ADS_CLIENT_SECRET env bindings",
        retryable: false,
      });
    }

    try {
      const tokenResponse = await refreshPinterestAdsToken(
        credentials.refreshToken,
        clientId,
        clientSecret,
      );
      const refreshTokenExpiresAt =
        tokenResponse.refresh_token_expires_in !== undefined
          ? new Date(Date.now() + tokenResponse.refresh_token_expires_in * 1000)
          : new Date(Date.now() + PINTEREST_ADS_REFRESH_TOKEN_LIFETIME_MS);
      return adapterOk({
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token ?? credentials.refreshToken,
        expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000),
        refreshTokenExpiresAt,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isInvalidGrant =
        err instanceof PinterestAdsTokenError
          ? err.oauthError === "invalid_grant"
          : /invalid_grant/i.test(message);
      return adapterErr({
        code: isInvalidGrant ? "token_expired" : "token_invalid",
        message: sanitizeErrorMessage(`Pinterest Ads token refresh failed: ${message}`),
        retryable: false,
      });
    }
  },

  /**
   * Catalog discovery (Story 19.4, hardened by Story 31.13).
   *
   * ## Single-tenant registry classification vs FULL catalog — intentional
   *
   * The registry (`registry.ts:191`) classifies pinterest-ads as
   * `oauthGrantScope: "single-tenant"` — V1-EFFECTIVE per the Story
   * 31-5/31-13 demotion (DEVIATION 2 rationale), with PROMOTION CRITERIA
   * ("promote to multi-tenant when 3+ paying customers request multiple
   * Pinterest Ads accounts"). That classification governs GRANT/auto-attach
   * treatment ONLY — it does not constrain discovery truthfulness. This
   * method deliberately returns the FULL owned + Business-Access-shared
   * catalog (bookmark-paginated to completion, 20-page defence cap,
   * BigInt-numeric ascending sort, deduped by id) as promotion
   * forward-compat: when the criteria fire, promotion is a registry-field
   * flip + attach-UI work — the catalog is already complete and
   * `metadata.permissions` already carries the owned-vs-shared signal for
   * the future picker. The deterministic primary pick (`accounts[0]` after
   * the numeric sort) lives in the OAuth callback
   * (`apps/server/src/routes/integrations/pinterest-ads-oauth.ts`), NOT here.
   *
   * ## Catalog contract notes (Story 31-4, `adapter.ts`)
   *
   * - `id` is FROZEN as the bare numeric Pinterest ad-account id (e.g.
   *   `"549755819515"`) — no prefixes, no composites. It is the
   *   `adAccountId` axis of `buildPinterestAdsCacheKey`, the
   *   `providerAccountId` the OAuth callback persists, and the
   *   `external_account_id` of catalog rows upserted by the 31-3 backfill;
   *   changing the format orphans those rows.
   * - `scopes` is display/consent metadata ONLY — never access control.
   * - An empty catalog (e.g. a PINNER-type user with no ad-account access)
   *   is a VALID success: `adapterOk([])`.
   */
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<AccountInfo[]>> {
    try {
      const result = await fetchPinterestAdsAdAccounts(credentials.accessToken);
      if (result.partialDiscovery) {
        // 20-page cap hit — the catalog is degraded (possibly truncated),
        // not failed, so we still return adapterOk. The structured warn is
        // the observability hook for the 31-24 re-discovery cron, mirroring
        // how the OAuth callback treats the same flag. tenantId/integrationId
        // are included so the event is correlatable when the cron fans out
        // across many grants (code review 31.13 R1).
        console.warn(
          JSON.stringify({
            event: "pinterest_ads_discovery_partial",
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            accountCount: result.accounts.length,
            message:
              "Pinterest Ads ad_accounts pagination hit the max-page cap; catalog may be truncated",
          }),
        );
      }
      const accounts: AccountInfo[] = result.accounts.map((acct) => ({
        id: acct.id,
        name: acct.name || `Account ${acct.id}`,
        platform: "pinterest-ads",
        // Single source of truth (AC #1): spread a FRESH array per entry —
        // PINTEREST_ADS_SCOPES is a readonly `as const` tuple and no caller
        // may mutate a shared reference into the catalog upsert.
        scopes: [...PINTEREST_ADS_SCOPES],
        metadata: {
          currency: acct.currency,
          country: acct.country,
          ownerUsername: acct.owner.username,
          agencyId: acct.agencyId,
          // Business-Access roles — owned-vs-shared signal for the future
          // 31-29 picker. Absent from the API → [] (never undefined: this
          // flows into external_account_metadata JSON).
          permissions: acct.permissions ?? [],
        },
      }));
      return adapterOk(accounts);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeErrorMessage(`Pinterest Ads account discovery failed: ${message}`),
        // Mirror validateConnection: 429/network blips are retryable;
        // 401-mapped token_expired stays non-retryable (Story 31.13 AC #8).
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  getAvailableMetrics(): MetricDefinition[] {
    return PINTEREST_ADS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return PINTEREST_ADS_DIMENSIONS;
  },
};

// ============================================================================
// Cache key (Story 19.4 — UNCHANGED, reused by Story 19.5)
// ============================================================================

export function buildPinterestAdsCacheKey(
  tenantId: string,
  adAccountId: string,
  dateRange: { start: Date; end: Date },
): string {
  const startIso = dateRange.start.toISOString().slice(0, 10);
  const endIso = dateRange.end.toISOString().slice(0, 10);
  return `pinterest-ads:${tenantId}:${adAccountId}:${startIso}:${endIso}`;
}

export { PINTEREST_ADS_REFRESH_TOKEN_LIFETIME_MS };

// ============================================================================
// Internal: ad-account metadata fetch
// ============================================================================

/**
 * Builds a typed `PinterestAdsApiCallError` from a `safeFetch` error envelope,
 * preserving the parsed `type` (`token_expired`/`rate_limited`/etc.) so the
 * caller can classify via `pinterestAdsErrorTypeToAdapterCode`.
 *
 * Same parsing strategy as `api.ts:buildPinterestAdsApiError` — replicated
 * here because that one is private. Centralised here so all the local
 * helpers (metadata fetch, entity lists) classify consistently.
 */
function buildTypedApiError(
  safeFetchError: { message: string; providerMessage?: string; providerStatus?: number },
  prefix: string,
): PinterestAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON
    }
  }
  const parsed = parsePinterestAdsApiError(parsedBody, safeFetchError.providerStatus);
  return new PinterestAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix,
  );
}

async function fetchAdAccountMetadata(
  accessToken: string,
  accountId: string,
): Promise<{ currency: string; timeZone: string }> {
  const result = await safeFetch(
    `${PINTEREST_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}`,
    { method: "GET", headers: buildPinterestHeaders(accessToken) },
    15_000,
  );
  if (!result.success) {
    throw buildTypedApiError(result.error, "Pinterest Ads ad-account metadata");
  }
  let json: { currency?: string; time_zone?: string };
  try {
    json = (await result.data.json()) as typeof json;
  } catch {
    // 200 OK but body is not JSON — Pinterest schema drift. Best-effort: log
    // a structured warning and proceed with empty metadata so the rest of the
    // fetchMetrics call still returns useful data.
    console.warn(
      JSON.stringify({
        event: "pinterest_ads_metadata_json_parse_failed",
        accountId,
        message: "ad_account metadata body was not JSON",
      }),
    );
    return { currency: "", timeZone: "" };
  }
  return { currency: json.currency ?? "", timeZone: json.time_zone ?? "" };
}

// ============================================================================
// Internal: top-N campaign / ad-group / ad ID discovery
// ============================================================================

interface TopEntityIds {
  campaigns: string[];
  adGroups: string[];
  ads: string[];
}

/**
 * BigInt-aware ascending comparator for tie-breaking spend-tied entities.
 * Mirrors `comparePinterestAccountIds` from `api.ts:308`.
 */
function compareIds(a: string, b: string): number {
  if (!/^\d+$/.test(a) || !/^\d+$/.test(b)) return a.localeCompare(b);
  const bigA = BigInt(a);
  const bigB = BigInt(b);
  if (bigA < bigB) return -1;
  if (bigA > bigB) return 1;
  return 0;
}

async function listEntities(
  url: URL,
  accessToken: string,
  label: string,
): Promise<Array<{ id: string }>> {
  const result = await safeFetch(
    url.toString(),
    { method: "GET", headers: buildPinterestHeaders(accessToken) },
    15_000,
  );
  if (!result.success) {
    throw buildTypedApiError(result.error, `Pinterest Ads ${label} list`);
  }
  let json: { items?: Array<{ id?: string | number }> };
  try {
    json = (await result.data.json()) as typeof json;
  } catch {
    return [];
  }
  return (json.items ?? [])
    .map((it) => ({ id: it.id != null ? String(it.id) : "" }))
    .filter((it) => it.id !== "");
}

/**
 * Discovers top-N campaign / ad-group / ad IDs ranked by spend over the
 * fetchMetrics date range (AC #10).
 *
 * Implementation:
 *   1. List all entities of each type (page_size=100; one call per type).
 *   2. If an entity type has more candidates than its limit, do ONE
 *      additional `*_analytics?columns=SPEND_IN_DOLLAR&granularity=TOTAL`
 *      call to score by spend over the date range.
 *   3. Sort descending by spend; tiebreak with BigInt-aware ascending ID
 *      comparator (so re-runs are deterministic across spend-tied entities).
 *   4. Take top N.
 *
 * If spend scoring fails (e.g. fresh account with no spend data yet), fall
 * back to BigInt-aware ascending ID order so the selection is still stable.
 */
async function fetchTopEntityIds(
  accessToken: string,
  accountId: string,
  dateRange: { start: Date; end: Date },
): Promise<TopEntityIds> {
  const campaignsUrl = new URL(
    `${PINTEREST_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/campaigns`,
  );
  campaignsUrl.searchParams.set("page_size", "100");

  const adGroupsUrl = new URL(
    `${PINTEREST_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/ad_groups`,
  );
  adGroupsUrl.searchParams.set("page_size", "100");

  const adsUrl = new URL(
    `${PINTEREST_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/ads`,
  );
  adsUrl.searchParams.set("page_size", "100");

  const [campaigns, adGroups, ads] = await Promise.all([
    listEntities(campaignsUrl, accessToken, "campaigns"),
    listEntities(adGroupsUrl, accessToken, "ad groups"),
    listEntities(adsUrl, accessToken, "ads"),
  ]);

  const startDate = dateToYearMonthDay(dateRange.start);
  const endDate = dateToYearMonthDay(dateRange.end);

  return {
    campaigns: await rankBySpend(
      accessToken,
      accountId,
      campaigns.map((c) => c.id),
      TOP_CAMPAIGNS_LIMIT,
      "campaigns",
      "CAMPAIGN_ID",
      startDate,
      endDate,
    ),
    adGroups: await rankBySpend(
      accessToken,
      accountId,
      adGroups.map((g) => g.id),
      TOP_AD_GROUPS_LIMIT,
      "ad_groups",
      "AD_GROUP_ID",
      startDate,
      endDate,
    ),
    ads: await rankBySpend(
      accessToken,
      accountId,
      ads.map((a) => a.id),
      TOP_ADS_LIMIT,
      "ads",
      "AD_ID",
      startDate,
      endDate,
    ),
  };
}

/**
 * One sync analytics call per entity type to score by spend over the date
 * range. Returns top-N IDs sorted by spend desc, BigInt-tiebroken on ID.
 *
 * - When the candidate pool is already ≤ limit, we skip the spend call to
 *   save a round-trip — the entity-scoped pipelines will accept all of them.
 * - When the spend call fails for any reason, we fall back to BigInt
 *   ascending ID order so the selection is still deterministic.
 */
async function rankBySpend(
  accessToken: string,
  accountId: string,
  candidateIds: string[],
  limit: number,
  endpoint: "campaigns" | "ad_groups" | "ads",
  idColumn: "CAMPAIGN_ID" | "AD_GROUP_ID" | "AD_ID",
  startDate: string,
  endDate: string,
): Promise<string[]> {
  if (candidateIds.length === 0) return [];
  const stableFallback = () => [...candidateIds].sort(compareIds).slice(0, limit);
  if (candidateIds.length <= limit) return stableFallback();

  // Cap at Pinterest's 100 IDs/request; for >100 we'd need pagination, but
  // since we already capped the entity list at 100 above, this is safe.
  const idsToScore = candidateIds.slice(0, 100);
  const url = new URL(
    `${PINTEREST_API_BASE}/ad_accounts/${encodeURIComponent(accountId)}/${endpoint}/analytics`,
  );
  url.searchParams.set("start_date", startDate);
  url.searchParams.set("end_date", endDate);
  url.searchParams.set("granularity", "TOTAL");
  url.searchParams.set("columns", `${idColumn},SPEND_IN_DOLLAR`);
  if (endpoint === "campaigns") url.searchParams.set("campaign_ids", idsToScore.join(","));
  if (endpoint === "ad_groups") url.searchParams.set("ad_group_ids", idsToScore.join(","));
  if (endpoint === "ads") url.searchParams.set("ad_ids", idsToScore.join(","));

  let json: { data?: Array<Record<string, unknown>>; summary?: Record<string, unknown> };
  try {
    const result = await safeFetch(
      url.toString(),
      { method: "GET", headers: buildPinterestHeaders(accessToken) },
      15_000,
    );
    if (!result.success) {
      // Let fatal errors propagate so the outer fetchMetrics surfaces a
      // single user-actionable signal (token_expired etc.). Non-fatal
      // failures degrade silently to the stable ID-order fallback. Parse
      // the typed error ONCE so fatality classification + error wire share
      // the same source of truth (Round 3 review).
      const typed = buildTypedApiError(result.error, `Pinterest Ads ${endpoint} spend ranking`);
      const code = pinterestAdsErrorTypeToAdapterCode(typed.type);
      if (FATAL_ERROR_CODES.has(code)) throw typed;
      return stableFallback();
    }
    json = (await result.data.json()) as typeof json;
  } catch (err) {
    // Re-throw typed errors that classify as fatal; degrade silently otherwise.
    if (err instanceof PinterestAdsApiCallError) {
      const code = pinterestAdsErrorTypeToAdapterCode(err.type);
      if (FATAL_ERROR_CODES.has(code)) throw err;
    }
    return stableFallback();
  }

  const rows = Array.isArray(json.data) ? json.data : [];
  if (rows.length === 0) return stableFallback();

  // Score: { id → spend }. Default unranked candidates to spend=0 so the
  // BigInt tiebreak still gives them a deterministic slot.
  const spendById = new Map<string, number>();
  for (const id of candidateIds) spendById.set(id, 0);
  for (const row of rows) {
    const id = row[idColumn] != null ? String(row[idColumn]) : "";
    const spend = Number(row.SPEND_IN_DOLLAR);
    if (id && Number.isFinite(spend)) spendById.set(id, spend);
  }

  return [...candidateIds]
    .sort((a, b) => {
      const sa = spendById.get(a) ?? 0;
      const sb = spendById.get(b) ?? 0;
      if (sa !== sb) return sb - sa; // desc by spend
      return compareIds(a, b); // BigInt tiebreak — stable across runs
    })
    .slice(0, limit);
}

// ============================================================================
// Internal: sync pipeline runner
// ============================================================================

interface SyncPipelineInput {
  reportType: PinterestAdsReportType;
  accessToken: string;
  accountId: string;
  dateRange: { start: Date; end: Date };
  topCampaignIds: ReadonlyArray<string>;
  topAdGroupIds: ReadonlyArray<string>;
  topAdIds: ReadonlyArray<string>;
}

async function runSyncPipeline(input: SyncPipelineInput): Promise<PipelineResult> {
  try {
    const request = buildSyncAnalyticsRequest({
      accountId: input.accountId,
      dateRange: input.dateRange,
      reportType: input.reportType,
    });

    let raw: { data?: ReadonlyArray<Record<string, unknown>>; summary?: Record<string, unknown> };
    switch (input.reportType) {
      case "overview":
        raw = await fetchPinterestAdsAccountAnalytics(
          input.accessToken,
          input.accountId,
          request.options,
        );
        break;
      case "campaigns":
        if (input.topCampaignIds.length === 0) {
          return {
            ok: false,
            fatal: false,
            reportType: input.reportType,
            reason: "No campaigns available for analytics",
          };
        }
        raw = await fetchPinterestAdsCampaignsAnalytics(
          input.accessToken,
          input.accountId,
          input.topCampaignIds,
          request.options,
        );
        break;
      case "adGroups":
        if (input.topAdGroupIds.length === 0) {
          return {
            ok: false,
            fatal: false,
            reportType: input.reportType,
            reason: "No ad groups available for analytics",
          };
        }
        raw = await fetchPinterestAdsAdGroupsAnalytics(
          input.accessToken,
          input.accountId,
          input.topAdGroupIds,
          request.options,
        );
        break;
      case "ads":
        if (input.topAdIds.length === 0) {
          return {
            ok: false,
            fatal: false,
            reportType: input.reportType,
            reason: "No ads available for analytics",
          };
        }
        raw = await fetchPinterestAdsAdsAnalytics(
          input.accessToken,
          input.accountId,
          input.topAdIds,
          request.options,
        );
        break;
      default:
        // keywords / ageGender / geo / interests — all targeting_analytics
        if (!request.targetingTypes || request.targetingTypes.length === 0) {
          return {
            ok: false,
            fatal: true,
            reportType: input.reportType,
            code: "invalid_response",
            message: `Pinterest Ads runSyncPipeline: missing targeting_types for "${input.reportType}"`,
            retryable: false,
          };
        }
        raw = await fetchPinterestAdsTargetingAnalytics(
          input.accessToken,
          input.accountId,
          request.targetingTypes,
          request.options,
        );
    }

    const rows = parsePinterestAdsSyncResponse(raw, input.reportType);
    const transformer = TRANSFORMERS[input.reportType];
    const records = transformer(rows);
    return { ok: true, reportType: input.reportType, records };
  } catch (err) {
    return classifyPipelineError(err, input.reportType);
  }
}

// ============================================================================
// Internal: async pipeline runner (with stale-URL recovery)
// ============================================================================

interface AsyncPipelineInput {
  reportType: PinterestAdsReportType;
  accessToken: string;
  accountId: string;
  dateRange: { start: Date; end: Date };
  requestedLevel?: string;
  requestedColumns?: ReadonlyArray<string>;
  topCampaignIds: ReadonlyArray<string>;
  topAdGroupIds: ReadonlyArray<string>;
  topAdIds: ReadonlyArray<string>;
}

async function runAsyncPipeline(input: AsyncPipelineInput): Promise<PipelineResult> {
  try {
    const requestedLevel = (input.requestedLevel ?? "ADVERTISER") as PinterestAdsAsyncReportLevel;
    // When the caller supplies an explicit column override, use it. Otherwise
    // fall back to the per-report-type sync column list so an "async-by-level"
    // call (e.g. `requestedLevel: "PIN_PROMOTION"` without explicit columns)
    // still has a sensible default.
    const syncRequest = buildSyncAnalyticsRequest({
      accountId: input.accountId,
      dateRange: input.dateRange,
      reportType: input.reportType,
    });
    const columns =
      input.requestedColumns && input.requestedColumns.length > 0
        ? input.requestedColumns
        : syncRequest.options.columns;
    // Always pass entity-scoping IDs when the level requires them — Pinterest
    // 400s on CAMPAIGN/AD_GROUP/AD without `campaign_ids[]`. Higher-level
    // reports (ADVERTISER / PIN_PROMOTION etc.) ignore the extra fields.
    const body = buildAsyncReportBody({
      level: requestedLevel,
      dateRange: input.dateRange,
      columns,
      granularity: syncRequest.options.granularity,
      campaignIds: input.topCampaignIds.length > 0 ? input.topCampaignIds : undefined,
      adGroupIds: input.topAdGroupIds.length > 0 ? input.topAdGroupIds : undefined,
      adIds: input.topAdIds.length > 0 ? input.topAdIds : undefined,
    });
    return await runAsyncJob(input, body);
  } catch (err) {
    return classifyPipelineError(err, input.reportType);
  }
}

async function runAsyncJob(
  input: AsyncPipelineInput,
  body: ReturnType<typeof buildAsyncReportBody>,
): Promise<PipelineResult> {
  const { token } = await createPinterestAdsAsyncReport(
    input.accessToken,
    input.accountId,
    body,
  );

  // Pin-promotion-level reports can take up to ~5 minutes; use the
  // extended poll budget per AC #9.
  const isLargeReport =
    body.level === "PIN_PROMOTION" ||
    body.level === "PRODUCT_GROUP" ||
    body.level === "PRODUCT_ITEM";
  const pollOpts = isLargeReport ? { maxAttempts: MAX_POLL_ATTEMPTS_LARGE } : undefined;

  // Poll → download. If the *download* itself fails because the signed S3
  // URL expired (Pinterest 403s with permission_denied after 5 min), re-poll
  // once for a fresh URL (mirrors `microsoft-ads.ts` stale-URL recovery).
  let downloadUrl = await pollPinterestAdsAsyncReportUntilDone(
    input.accessToken,
    input.accountId,
    token,
    pollOpts,
  );
  let payload: unknown;
  try {
    payload = await downloadPinterestAdsAsyncReport(downloadUrl);
  } catch (downloadErr) {
    // Stale download URL = permission_denied from the signed URL. EXPIRED
    // status comes from the *poll* and would have thrown above already.
    const isStaleUrl =
      downloadErr instanceof PinterestAdsApiCallError &&
      downloadErr.type === "permission_denied";
    if (!isStaleUrl) throw downloadErr;
    downloadUrl = await pollPinterestAdsAsyncReportUntilDone(
      input.accessToken,
      input.accountId,
      token,
      pollOpts,
    );
    payload = await downloadPinterestAdsAsyncReport(downloadUrl);
  }

  // Pinterest's async JSON download envelope shape varies across report
  // types. Three shapes observed in the wild + Python SDK fixtures:
  //   - top-level Array<Record>             (most common)
  //   - { data: Array<Record>, ... }        (matches sync analytics envelope)
  //   - { rows: Array<Record>, ... }        (some product-group reports)
  // Anything else → empty rows so we surface a non-fatal warning instead of
  // crashing with a parse error.
  const candidate =
    Array.isArray(payload)
      ? payload
      : Array.isArray((payload as { data?: unknown })?.data)
        ? (payload as { data: unknown[] }).data
        : Array.isArray((payload as { rows?: unknown })?.rows)
          ? (payload as { rows: unknown[] }).rows
          : [];
  const rows = candidate.filter(
    (r): r is Record<string, unknown> => typeof r === "object" && r !== null,
  );
  const transformer = TRANSFORMERS[input.reportType];
  const records = transformer(rows);
  return { ok: true, reportType: input.reportType, records };
}

// ============================================================================
// Internal: error classification (shared by sync + async)
// ============================================================================

function classifyPipelineError(
  err: unknown,
  reportType: PinterestAdsReportType,
): PipelineResult {
  if (err instanceof PinterestAdsApiCallError) {
    const code = pinterestAdsErrorTypeToAdapterCode(err.type);
    if (FATAL_ERROR_CODES.has(code)) {
      return {
        ok: false,
        fatal: true,
        reportType,
        code,
        message: sanitizeErrorMessage(`Pinterest Ads ${reportType} report failed: ${err.message}`),
        retryable: err.retryable,
      };
    }
    return {
      ok: false,
      fatal: false,
      reportType,
      reason: sanitizeErrorMessage(err.message),
    };
  }
  return {
    ok: false,
    fatal: false,
    reportType,
    reason: sanitizeErrorMessage(err instanceof Error ? err.message : String(err)),
  };
}

// ============================================================================
// Fixture-mode helper (lazy-imports the fixtures sub-package, mirrors MS Ads)
// ============================================================================

async function loadPinterestAdsFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date },
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Pinterest Ads demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve demo client slug",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import("../fixtures/kv-loader");
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId,
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Pinterest Ads demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "pinterest-ads",
    clientSlug,
  );

  if (metrics === null) {
    return adapterErr({
      code: "account_not_found",
      message: `Pinterest Ads demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(metrics, dateRange.start, dateRange.end);
  return adapterOk(filtered);
}

// ============================================================================
// Error Code Inference (Story 19.4 — UNCHANGED)
// ============================================================================

function adapterErrorCodeFromError(err: unknown): AdapterErrorCode {
  if (err instanceof PinterestAdsApiCallError) {
    return pinterestAdsErrorTypeToAdapterCode(err.type);
  }
  const message = err instanceof Error ? err.message : String(err);
  if (/timed out|Network error|fetch failed/i.test(message)) {
    return "network_error";
  }
  return "invalid_response";
}
